import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'bootstrap';
import card1 from '../../../assets/cashier/images/paymentsuccessicon.png';
import card2 from '../../../assets/cashier/images/card2-img.svg';
import HomeIcon from '../../../assets/cashier/images/home-img.svg';
import InvoiceIcon from '../../../assets/cashier/images/invoice-img.svg';
import PayIcon from '../../../assets/cashier/images/pay-img.svg';
import ReprintIcon from '../../../assets/cashier/images/reprint-img.svg';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { showPrice } from '../../../utils/commonfunction';
import ReactToPrint from 'react-to-print';
import { detail, receiptDetail, transactionDetail } from '../services/table.services';
import { transactionDetail as salesTransactionDetail } from '../services/sales.service';
import sucessIcon from '../../../assets/cashier/images/success.png';
import { CASHIER_ORDER_FLOW_TYPES, TRANSACTION_PRINT_TYPES, SWAL_SETTINGS } from '../../../utils/Constants';
import { useSelector } from 'react-redux';
import {useCustomerDisplay} from '../shared/customer-display/useCustomerDisplay';
import {publishDisplayEvent} from '../shared/customer-display/customerDisplayClient';
import Swal from 'sweetalert2';
import SalesSlipViewModal from '../components/SalesSlipViewModal';

const PRINT_PREVIEW_MODAL_ID = 'payment-print-preview-modal';

export default function PaymentSuccess() {
  const { t , i18n} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const splitPaymentSuccessState = location.state || {};
  const splitPaymentReturnPath = splitPaymentSuccessState?.returnPath;
  const [orderTransactionDetail, setOrderTransactionDetail] = useState(splitPaymentSuccessState?.orderTransactionDetail || {});
  const componentRef = useRef();
  const reprintRef = useRef();
  const [langChanged, setLangChanged] = useState(false);
  const [printerDetails , setPrinterDetails] = useState({});
  const restSettingDetails = useSelector((state) => state?.restSettings?.data);
  const effectiveMethod = orderTransactionDetail?.payment_method ?? splitPaymentSuccessState?.paymentMethod ?? 'cash';
  const displayTotal = Number(orderTransactionDetail?.amount_after_discount
    ?? orderTransactionDetail?.great_grand_total
    ?? orderTransactionDetail?.received_amount
    ?? 0) || 0;
  useCustomerDisplay(Object.keys(orderTransactionDetail || {}).length ? (effectiveMethod === 'cash'
    ? {type: 'cash-complete', tendered: Number(orderTransactionDetail?.received_amount) || displayTotal, change: Number(orderTransactionDetail?.returned_amount) || 0}
    : {type: 'card-complete', total: displayTotal}) : undefined);

  // Print-preview popup: clicking Print Invoice / Print Receipt no longer prints
  // immediately — it opens the same slip-view modal Sales History uses (Cancel +
  // Print only) so the cashier can confirm before the document is sent.
  const closePrintPreviewRef = useRef();
  const printSlipPreviewRef = useRef();
  const [printPreviewData, setPrintPreviewData] = useState(null);
  const [printPreviewType, setPrintPreviewType] = useState(TRANSACTION_PRINT_TYPES.RECEIPT);
  const printInFlightRef = useRef(false);
  const fetchedOrderIdRef = useRef(undefined);



  // Printing happens backend-side: passing printReceipt enqueues a print job
  // (billing or royushu, per `type`) that the cashier tablet claims and prints.
  // The installment this screen was reached through, when it came from a split
  // payment. Reprinting has to name it, or the backend builds a whole-order
  // receipt and a partial payment reprints as a final one.
  const currentTransactionId = splitPaymentSuccessState?.orderTransactionDetail?._id;

  const getOrderDetail = (orderId , type='receipt', shouldPrint, transactionId) => {
    transactionDetail({ o_id: orderId , type : type , printReceipt: shouldPrint, transaction_id: transactionId})
      .then((response) => {
        setOrderTransactionDetail(response?.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };



  useEffect(() => {
    if (splitPaymentSuccessState?.orderTransactionDetail) {
      setOrderTransactionDetail(splitPaymentSuccessState.orderTransactionDetail);
      return;
    }

    if (params.orderId) {
      if (fetchedOrderIdRef.current === params.orderId) {
        return;
      }
      fetchedOrderIdRef.current = params.orderId;
      getOrderDetail(params.orderId, 'receipt', false);
    }
    else{
      navigate("/cashier/dashboard")
    }
  }, [params.orderId, splitPaymentSuccessState?.orderTransactionDetail]);

  // Language toggle only needs to re-fetch the receipt copy in the new language —
  // it must not enqueue another print job, so it skips the mount-time run (the
  // effect above already prints once) and always calls getOrderDetail with
  // shouldPrint=false.
  const isFirstLangRun = useRef(true);
  useEffect(() => {
    if (isFirstLangRun.current) {
      isFirstLangRun.current = false;
      return;
    }
    if (params.orderId && !splitPaymentSuccessState?.orderTransactionDetail) {
      getOrderDetail(params.orderId, 'receipt', false);
    }
  }, [langChanged]);

  const handleSuccessExit = () => {
    publishDisplayEvent({type: 'idle'}, true);
    const returnFlowType = splitPaymentReturnPath?.includes(`type=${CASHIER_ORDER_FLOW_TYPES.SPLIT_PAY}`)
      ? CASHIER_ORDER_FLOW_TYPES.SPLIT_PAY
      : CASHIER_ORDER_FLOW_TYPES.EDIT_ORDER;

    navigate(
      splitPaymentReturnPath || "/cashier/dashboard",
      splitPaymentReturnPath ? { replace: true, state: { type: returnFlowType } } : undefined,
    );
  };


  useMemo(()=>{
    document.addEventListener('language_change',function(){
      setLangChanged(!langChanged);
    })
  });

  // The transaction id this success screen is showing — the same id Sales
  // History's row uses as `o_id` when it fetches/prints a slip.
  const activeTransactionId = currentTransactionId || orderTransactionDetail?._id;

  const openPrintPreview = async (type) => {
    if (!activeTransactionId) {
      return;
    }
    try {
      const response = await salesTransactionDetail({ o_id: activeTransactionId, printReceipt: false, type });
      setPrintPreviewData(response?.data || null);
      setPrintPreviewType(type);
      Modal.getOrCreateInstance(document.getElementById(PRINT_PREVIEW_MODAL_ID)).show();
    } catch (err) {
      console.log(err);
      Swal.fire({ icon: "error", text: t("msg_reprint_failed"), ...SWAL_SETTINGS });
    }
  };

  const confirmPrintPreview = async () => {
    if (!activeTransactionId || printInFlightRef.current) {
      return;
    }
    printInFlightRef.current = true;
    try {
      await salesTransactionDetail({ o_id: activeTransactionId, printReceipt: true, type: printPreviewType });
      Swal.fire({ icon: "success", text: t("msg_reprint_queued"), ...SWAL_SETTINGS });
    } catch (err) {
      console.log(err);
      Swal.fire({ icon: "error", text: t("msg_reprint_failed"), ...SWAL_SETTINGS });
    } finally {
      printInFlightRef.current = false;
      closePrintPreviewRef.current?.click();
    }
  };

  return (
    <div className="dashboard-cashier-wrap">
      <div className="cashier-head text-center">
        <h4>{t("payment_label")}</h4>
        <div className="btns-head-in-left" onClick={handleSuccessExit}>
          <button className="btn btn-icon">
            <i className="ti ti-arrow-left"></i>
          </button>
        </div>
      </div>

      <div className="payment-wraper">
        <div className="payment-wraper-in">
          {orderTransactionDetail.payment_method === "cash" && (
            <div className="paymnet-typing shadow-box">
              <span>{t("label_change_to_return")}</span>
              <h4>¥ {showPrice(orderTransactionDetail?.returned_amount)}</h4>
            </div>
          )}

          <div className="payment-keypad-box shadow-box">
            <div className="paymnet-success-msg">
              {orderTransactionDetail.payment_method === "cash" ? (
                <figure>
                  <img src={card1} />
                </figure>
              ) : (
                <img width="100px" src={sucessIcon} />
              )}

              {orderTransactionDetail.payment_method !== "cash" && (
                <div className="d-flex flex-column align-items-center ">
                  <span style={{ fontSize: "18px", color: "black" }}>Order no. # {orderTransactionDetail?.orderDetail?.order_id}</span>
                  <span className="m-2" style={{ fontSize: "50px", fontWeight: "bold", color: "black" }}>
                    ¥ {showPrice(orderTransactionDetail?.received_amount)}
                  </span>
                </div>
              )}
              <h4>{t("payment_success_msg")}</h4>

              {orderTransactionDetail.payment_method !== "cash" && (
                <div className="">
                  <button className="btn btn-primary" onClick={handleSuccessExit}>
                    {t('back_home')}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="keypad-action-btn-left gap-3">
           

            <button
              className="keypad-action-btns"
              onClick={() => {
                openPrintPreview(TRANSACTION_PRINT_TYPES.ROYUSHU);
              }}
            >
              <img src={InvoiceIcon} />
              <span>{t("label_print_invoice")}</span>
            </button>


            <button
                  className="keypad-action-btns mt-5"
                  onClick={() => {
                    openPrintPreview(TRANSACTION_PRINT_TYPES.RECEIPT);
                  }}
                >
                  <img src={ReprintIcon} />
                  <span>{t("label_print_receipt")}</span>
                </button>
          </div>
          <div className="keypad-action-btn-right gap-3">
            <button className="keypad-action-btns" onClick={handleSuccessExit}>
              <img src={HomeIcon} />
              <span>{t("nav_link_home")}</span>
            </button>
          </div>
        </div>
      </div>
      {/* style={{ display: 'none' }} */}
      <div style={{ display: "none" }}>
        <div ref={componentRef} className="container d-flex justify-content-center align-items-center vh-100">
          <div dangerouslySetInnerHTML={{ __html: orderTransactionDetail?.html ? orderTransactionDetail?.html : "N/A" }}></div>
        </div>
      </div>

      <div style={{ display: "none" }}>
        <div ref={reprintRef} className="container d-flex justify-content-center align-items-center vh-100">
          <div dangerouslySetInnerHTML={{ __html: orderTransactionDetail?.reprint_html ? orderTransactionDetail?.reprint_html : "N/A" }}></div>
        </div>
      </div>

      <div className="modal fade" data-bs-backdrop="static" id={PRINT_PREVIEW_MODAL_ID} tabIndex="-1" aria-hidden="true">
        <SalesSlipViewModal
          mode="printOnly"
          transactionData={printPreviewData}
          printType={printPreviewType}
          closeModalRef={closePrintPreviewRef}
          printSlipRef={printSlipPreviewRef}
          onPrint={confirmPrintPreview}
        />
      </div>
    </div>
  );
}
